import { Stack } from '@mui/material'
import { FormEvent, useEffect, useState } from 'react'
import axAPI from '../../httpConfig'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { IRationTableComponents } from '../../models/IRationSystem'
import { notifyUser } from '../../store/notificationsSlice'
import { fetchRationComponents } from '../../store/rationSystemSlice'
import { ButtonUI } from '../../ui/buttons/ButtonUI'
import Drawer from '../../ui/Drawer'
import { TypographyPrimary } from '../../ui/typography/TypographyPrimary'
import {
	FloatingField,
	getDuplicateIndexes,
	isCompleteDryMatter,
	sanitizeDryMatterInput,
} from './RationComponentField'

type PopupRationAddCompProps = {
	openAddComp: boolean
	setOpenAddComp: (open: boolean) => void
	onCreated?: (component: IRationTableComponents) => void
}

const PopupRationAddComp = ({
	openAddComp,
	setOpenAddComp,
	onCreated,
}: PopupRationAddCompProps) => {
	const dispatch = useAppDispatch()
	const rationComponents = useAppSelector(
		state => state.rationSystemReducer.rationComponents,
	)
	const [title, setTitle] = useState('')
	const [dryMatter, setDryMatter] = useState('')
	const [titleError, setTitleError] = useState('')
	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		if (!openAddComp) {
			setTitle('')
			setDryMatter('')
			setTitleError('')
			setIsSaving(false)
		}
	}, [openAddComp])

	const canSave =
		title.trim() !== '' && isCompleteDryMatter(dryMatter) && !isSaving

	const notifySaveError = () => {
		dispatch(
			notifyUser({
				title: 'Произошла ошибка',
				description: 'Попробуйте снова.',
				isError: true,
			}),
		)
	}

	const handleDryMatterChange = (rawValue: string) => {
		const inputValue = sanitizeDryMatterInput(rawValue)
		if (inputValue !== null) {
			setDryMatter(inputValue)
		}
	}

	const onSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (!canSave) return

		const normalizedTitle = title.trim()
		const isDuplicate = rationComponents?.some(
			component => component.title.trim() === normalizedTitle,
		)

		if (isDuplicate) {
			setTitleError('Данное название уже используется')
			notifySaveError()
			return
		}

		setIsSaving(true)
		axAPI({
			method: 'POST',
			url: '/ration/components',
			data: [
				{
					title: normalizedTitle,
					dry_matter_percent: Number(dryMatter),
				},
			],
		})
			.then(response => {
				if (response.status === 201 || response.status === 200) {
					dispatch(fetchRationComponents())
						.unwrap()
						.then((components: IRationTableComponents[]) => {
							const created = components.find(
								component => component.title.trim() === normalizedTitle,
							)
							if (created) onCreated?.(created)
						})
						.catch(() => undefined)
					dispatch(
						notifyUser({
							title: `Компонент «${normalizedTitle}» создан`,
							description: '',
							isError: false,
						}),
					)
					setOpenAddComp(false)
					return
				}

				notifySaveError()
			})
			.catch(error => {
				if (getDuplicateIndexes(error).includes(0)) {
					setTitleError('Данное название уже используется')
				}
				notifySaveError()
			})
			.finally(() => {
				setIsSaving(false)
			})
	}

	return (
		<Drawer
			open={openAddComp}
			onClose={() => setOpenAddComp(false)}
			title='Создание компонента'
			anchor='right'
			width={440}
		>
			<Stack
				component='form'
				onSubmit={onSubmit}
				sx={{
					flex: 1,
					minHeight: '100%',
					px: '24px',
					boxSizing: 'border-box',
				}}
			>
				<Stack gap='20px'>
					<Stack gap='24px'>
						<TypographyPrimary sx={{ fontWeight: 600 }}>
							Компонент
						</TypographyPrimary>
						<FloatingField
							value={title}
							placeholder='Название компонента'
							error={Boolean(titleError)}
							helperText={titleError}
							onChange={nextTitle => {
								setTitle(nextTitle)
								setTitleError('')
							}}
						/>
					</Stack>
					<Stack gap='24px' alignItems='flex-start'>
						<TypographyPrimary sx={{ fontWeight: 600 }}>
							СВ, %
						</TypographyPrimary>
						<FloatingField
							value={dryMatter}
							placeholder='СВ, %'
							width='160px'
							suffix='%'
							centerWhenEmpty
							onChange={handleDryMatterChange}
						/>
					</Stack>
				</Stack>
				<Stack sx={{ mt: 'auto', pb: '24px', pt: '24px' }}>
					<ButtonUI
						type='submit'
						disabled={!canSave}
						sx={{ height: '56px', width: '100%' }}
					>
						Сохранить
					</ButtonUI>
				</Stack>
			</Stack>
		</Drawer>
	)
}

export default PopupRationAddComp
